import type { Scope } from '../types/governance'

/**
 * Administrative records for Social & Campus Life.
 *
 * Everything here is campus-scoped: a moderation case, a community approval and
 * an analytics figure all carry the campus they belong to, so the console can
 * refuse cross-campus access rather than filtering it away at display time.
 *
 * Seeded development data — none of these figures are production metrics.
 */

export type ApprovalItemKind = 'community' | 'club' | 'organisation' | 'challenge'
export type ApprovalItemState = 'pending' | 'approved' | 'declined' | 'more_info'

export interface SocialApprovalItem {
  id: string
  kind: ApprovalItemKind
  name: string
  requestedBy: string
  requestedAt: string
  campusId: string
  campusLabel: string
  scope: Scope
  category: string
  purpose: string
  /** What the requester claims, kept separate from what has been checked. */
  claim: string
  /** What operations has actually confirmed. Empty until someone checks. */
  confirmed: string[]
  state: ApprovalItemState
  decidedBy: string | null
  decisionReason: string | null
}

export const socialApprovals: SocialApprovalItem[] = [
  {
    id: 'sap-1',
    kind: 'club',
    name: 'FUTA Robotics Society',
    requestedBy: '@adaeze.n',
    requestedAt: '2026-08-11T09:20:00Z',
    campusId: 'cmp-futa-main',
    campusLabel: 'FUTA Akure — Main Campus',
    scope: { level: 'campus', tenantId: 'tnt-futa', institutionId: 'inst-futa', campusId: 'cmp-futa-main' },
    category: 'Academic',
    purpose: 'Weekly build sessions, competition entries and a lending library of components.',
    claim: 'Registered with the Students Affairs Division in 2024.',
    confirmed: [],
    state: 'pending',
    decidedBy: null,
    decisionReason: null,
  },
  {
    id: 'sap-2',
    kind: 'community',
    name: 'Aule Residence',
    requestedBy: '@tunde.b',
    requestedAt: '2026-08-12T14:05:00Z',
    campusId: 'cmp-futa-main',
    campusLabel: 'FUTA Akure — Main Campus',
    scope: { level: 'campus', tenantId: 'tnt-futa', institutionId: 'inst-futa', campusId: 'cmp-futa-main' },
    category: 'Campus',
    purpose: 'Coordination for students living in the Aule area — transport, security notices, shared costs.',
    claim: 'Student-run. Not claiming to represent the institution.',
    confirmed: ['Requester is a verified FUTA student', 'No duplicate community for this area'],
    state: 'pending',
    decidedBy: null,
    decisionReason: null,
  },
  {
    id: 'sap-3',
    kind: 'organisation',
    name: 'Akure Tech Founders',
    requestedBy: '@kelechi.i',
    requestedAt: '2026-08-08T11:40:00Z',
    campusId: 'cmp-futa-main',
    campusLabel: 'FUTA Akure — Main Campus',
    scope: { level: 'campus', tenantId: 'tnt-futa', institutionId: 'inst-futa', campusId: 'cmp-futa-main' },
    category: 'Career',
    purpose: 'Mentorship sessions and an internship pipeline for final-year students.',
    claim: 'External organisation seeking a verified presence on campus.',
    confirmed: ['Business registration document supplied'],
    state: 'more_info',
    decidedBy: '@mary.o',
    decisionReason:
      'Registration supplied, but no institutional sponsor named. An external body cannot hold a campus presence without one.',
  },
  {
    id: 'sap-4',
    kind: 'challenge',
    name: 'Inter-hall Debate Cup',
    requestedBy: '@esa.futa',
    requestedAt: '2026-08-05T08:10:00Z',
    campusId: 'cmp-akoka',
    campusLabel: 'UNILAG — Akoka',
    scope: {
      level: 'campus',
      tenantId: 'tnt-unilag',
      institutionId: 'inst-unilag',
      campusId: 'cmp-akoka',
    },
    category: 'Social',
    purpose: 'Team competition across halls of residence with published judging criteria.',
    claim: 'Organised by the verified Students Association.',
    confirmed: ['Organiser is a verified club', 'Judging criteria published in advance'],
    state: 'approved',
    decidedBy: '@david.a',
    decisionReason: 'Organiser verified and criteria published before registration opened.',
  },
]

/* ------------------------------------------------------------- moderation */

export type SocialCaseState = 'open' | 'under_review' | 'actioned' | 'dismissed' | 'appealed'
export type SocialCaseCategory =
  | 'Harassment'
  | 'Spam'
  | 'Scam'
  | 'Impersonation'
  | 'Abusive content'
  | 'Dangerous content'
  | 'Misleading information'
  | 'Privacy violation'
  | 'Unwanted contact'

export interface SocialCase {
  id: string
  queue: 'Posts' | 'Comments' | 'Communities' | 'Clubs' | 'People' | 'Stories'
  category: SocialCaseCategory
  subject: string
  excerpt: string
  reportedBy: string
  reportedAt: string
  campusId: string
  campusLabel: string
  scope: Scope
  state: SocialCaseState
  priority: 'high' | 'normal' | 'low'
  /** Automated signals are inputs to a human decision, never the decision. */
  signals: string[]
  assignedTo: string | null
  action: string | null
  actionReason: string | null
  /** Reversible: an appeal restores the content if upheld. */
  appealNote: string | null
}

export const socialCases: SocialCase[] = [
  {
    id: 'scs-1',
    queue: 'Posts',
    category: 'Scam',
    subject: 'Post by @quickdeals.ng',
    excerpt:
      '“Pay a ₦5,000 inspection fee and I will hold the room for you until you arrive on campus.”',
    reportedBy: '@ifeoma.e',
    reportedAt: '2026-08-13T16:45:00Z',
    campusId: 'cmp-futa-main',
    campusLabel: 'FUTA Akure — Main Campus',
    scope: { level: 'campus', tenantId: 'tnt-futa', institutionId: 'inst-futa', campusId: 'cmp-futa-main' },
    state: 'open',
    priority: 'high',
    signals: [
      'Account is 4 days old',
      'Same text posted in 6 communities within an hour',
      'Requests payment outside Campuzon',
    ],
    assignedTo: null,
    action: null,
    actionReason: null,
    appealNote: null,
  },
  {
    id: 'scs-2',
    queue: 'Comments',
    category: 'Harassment',
    subject: 'Comment thread on “Where should the department social hold?”',
    excerpt: 'Reported for repeated personal abuse directed at one student.',
    reportedBy: '@chidera.o',
    reportedAt: '2026-08-13T10:12:00Z',
    campusId: 'cmp-futa-main',
    campusLabel: 'FUTA Akure — Main Campus',
    scope: { level: 'campus', tenantId: 'tnt-futa', institutionId: 'inst-futa', campusId: 'cmp-futa-main' },
    state: 'under_review',
    priority: 'high',
    signals: ['Three separate reporters', 'Same target named in each report'],
    assignedTo: '@david.a',
    action: null,
    actionReason: null,
    appealNote: null,
  },
  {
    id: 'scs-3',
    queue: 'Communities',
    category: 'Impersonation',
    subject: '“FUTA Official Admissions”',
    excerpt: 'Community name implies it is run by the institution. It is student-run.',
    reportedBy: '@registry.futa',
    reportedAt: '2026-08-10T09:00:00Z',
    campusId: 'cmp-futa-main',
    campusLabel: 'FUTA Akure — Main Campus',
    scope: { level: 'campus', tenantId: 'tnt-futa', institutionId: 'inst-futa', campusId: 'cmp-futa-main' },
    state: 'actioned',
    priority: 'high',
    signals: ['Name matches a protected institutional term'],
    assignedTo: '@david.a',
    action: 'Renamed and marked student-run',
    actionReason:
      'The community may continue, but it may not carry a name that implies institutional authority. Members were told why.',
    appealNote: null,
  },
  {
    id: 'scs-4',
    queue: 'Posts',
    category: 'Misleading information',
    subject: 'Post claiming a registration deadline moved',
    excerpt: '“Registration has been extended to the 30th — no need to rush.”',
    reportedBy: '@tunde.b',
    reportedAt: '2026-08-12T13:30:00Z',
    campusId: 'cmp-futa-main',
    campusLabel: 'FUTA Akure — Main Campus',
    scope: { level: 'campus', tenantId: 'tnt-futa', institutionId: 'inst-futa', campusId: 'cmp-futa-main' },
    state: 'appealed',
    priority: 'normal',
    signals: ['Contradicts the published academic calendar'],
    assignedTo: '@david.a',
    action: 'Removed',
    actionReason: 'Contradicted the published calendar on a time-sensitive matter.',
    appealNote:
      'Author says they were relaying a departmental notice. Awaiting confirmation from the department before the removal stands or is reversed.',
  },
  {
    id: 'scs-5',
    queue: 'People',
    category: 'Unwanted contact',
    subject: 'Repeated connection requests after a block',
    excerpt: 'Reported for creating a second account to route around a block.',
    reportedBy: '@amaka.u',
    reportedAt: '2026-08-09T19:20:00Z',
    campusId: 'cmp-akoka',
    campusLabel: 'UNILAG — Akoka',
    scope: {
      level: 'campus',
      tenantId: 'tnt-unilag',
      institutionId: 'inst-unilag',
      campusId: 'cmp-akoka',
    },
    state: 'open',
    priority: 'high',
    signals: ['Two accounts share a device fingerprint', 'Block in place on the first account'],
    assignedTo: null,
    action: null,
    actionReason: null,
    appealNote: null,
  },
  {
    id: 'scs-6',
    queue: 'Stories',
    category: 'Privacy violation',
    subject: 'Story showing a hostel room number and a student’s timetable',
    excerpt: 'Reported by the student pictured.',
    reportedBy: '@ifeoma.e',
    reportedAt: '2026-08-07T21:05:00Z',
    campusId: 'cmp-futa-main',
    campusLabel: 'FUTA Akure — Main Campus',
    scope: { level: 'campus', tenantId: 'tnt-futa', institutionId: 'inst-futa', campusId: 'cmp-futa-main' },
    state: 'dismissed',
    priority: 'normal',
    signals: ['Story already expired before review'],
    assignedTo: '@david.a',
    action: 'No action — content had expired',
    actionReason:
      'The story expired on its own within 24 hours. The reporter was told, and the account was given a privacy reminder rather than a penalty.',
    appealNote: null,
  },
]

/* -------------------------------------------------------------- taxonomy */

export interface TaxonomyEntry {
  id: string
  set: 'Community categories' | 'Club categories' | 'Challenge categories' | 'Report categories'
  value: string
  origin: 'Global' | 'Tenant override' | 'Campus override'
  inUse: number
  editableAt: 'Global' | 'Tenant' | 'Campus'
}

export const socialTaxonomy: TaxonomyEntry[] = [
  { id: 'tx-1', set: 'Community categories', value: 'Academic', origin: 'Global', inUse: 42, editableAt: 'Global' },
  { id: 'tx-2', set: 'Community categories', value: 'Social', origin: 'Global', inUse: 28, editableAt: 'Global' },
  { id: 'tx-3', set: 'Community categories', value: 'Interest', origin: 'Global', inUse: 31, editableAt: 'Global' },
  { id: 'tx-4', set: 'Community categories', value: 'Career', origin: 'Global', inUse: 12, editableAt: 'Global' },
  { id: 'tx-5', set: 'Community categories', value: 'Campus', origin: 'Global', inUse: 19, editableAt: 'Global' },
  {
    id: 'tx-6',
    set: 'Community categories',
    value: 'Postgraduate research',
    origin: 'Tenant override',
    inUse: 3,
    editableAt: 'Tenant',
  },
  { id: 'tx-7', set: 'Challenge categories', value: 'Academic', origin: 'Global', inUse: 6, editableAt: 'Global' },
  { id: 'tx-8', set: 'Challenge categories', value: 'Sport', origin: 'Global', inUse: 4, editableAt: 'Global' },
  {
    id: 'tx-9',
    set: 'Challenge categories',
    value: 'Creative',
    origin: 'Campus override',
    inUse: 2,
    editableAt: 'Campus',
  },
  { id: 'tx-10', set: 'Report categories', value: 'Harassment', origin: 'Global', inUse: 0, editableAt: 'Global' },
  { id: 'tx-11', set: 'Report categories', value: 'Scam', origin: 'Global', inUse: 0, editableAt: 'Global' },
  {
    id: 'tx-12',
    set: 'Report categories',
    value: 'Privacy violation',
    origin: 'Global',
    inUse: 0,
    editableAt: 'Global',
  },
]

/* ---------------------------------------------------- achievement register */

export interface AchievementDefinition {
  id: string
  label: string
  /** The record that proves it. An achievement with no proving record is not offered. */
  earnedFrom: string
  awardedBy: 'System from records' | 'Club officer' | 'Institution'
  active: boolean
  awarded: number
}

export const achievementDefinitions: AchievementDefinition[] = [
  {
    id: 'ach-1',
    label: 'Event volunteer',
    earnedFrom: 'Organiser-confirmed volunteer record on a published event',
    awardedBy: 'Club officer',
    active: true,
    awarded: 84,
  },
  {
    id: 'ach-2',
    label: 'Club officer',
    earnedFrom: 'Named officer on a verified club',
    awardedBy: 'Institution',
    active: true,
    awarded: 31,
  },
  {
    id: 'ach-3',
    label: 'Hackathon participant',
    earnedFrom: 'Submission recorded against a challenge entry',
    awardedBy: 'System from records',
    active: true,
    awarded: 46,
  },
  {
    id: 'ach-4',
    label: 'Community contributor',
    earnedFrom: 'Answers marked helpful by the person who asked, over 90 days',
    awardedBy: 'System from records',
    active: true,
    awarded: 22,
  },
  {
    id: 'ach-5',
    label: 'Mentor',
    earnedFrom: 'Completed mentorship sessions in Marketplace',
    awardedBy: 'System from records',
    active: true,
    awarded: 9,
  },
  {
    id: 'ach-6',
    label: 'Daily streak',
    earnedFrom: 'Opening the app on consecutive days',
    awardedBy: 'System from records',
    active: false,
    awarded: 0,
  },
]

/* ------------------------------------------------------------- analytics */

export interface SocialMetric {
  id: string
  label: string
  value: string
  basis: string
  /** Whether this measures utility or merely attention. */
  measures: 'participation' | 'utility' | 'safety' | 'reach'
}

export const socialMetrics: SocialMetric[] = [
  {
    id: 'sm-1',
    label: 'Students who joined a community',
    value: '612 of 1,480',
    basis: 'Distinct accounts with at least one active membership',
    measures: 'participation',
  },
  {
    id: 'sm-2',
    label: 'Event attendance confirmed',
    value: '318',
    basis: 'Organiser-confirmed attendance records, not RSVPs',
    measures: 'participation',
  },
  {
    id: 'sm-3',
    label: 'Questions resolved',
    value: '74 of 96',
    basis: 'Marked resolved by the person who asked',
    measures: 'utility',
  },
  {
    id: 'sm-4',
    label: 'Connections that led to a conversation',
    value: '241',
    basis: 'Accepted connections with at least one message exchanged',
    measures: 'utility',
  },
  {
    id: 'sm-5',
    label: 'Social posts that opened a marketplace order',
    value: '38',
    basis: 'Orders whose first touch was a social post or gig request',
    measures: 'utility',
  },
  {
    id: 'sm-6',
    label: 'Reports raised',
    value: '27',
    basis: 'All social surfaces, this campus, last 30 days',
    measures: 'safety',
  },
  {
    id: 'sm-7',
    label: 'Reports actioned within 24 hours',
    value: '21 of 27',
    basis: 'Time from report to a recorded human decision',
    measures: 'safety',
  },
  {
    id: 'sm-8',
    label: 'Posts published',
    value: '486',
    basis: 'Published, not including drafts or removed content',
    measures: 'reach',
  },
]

/* -------------------------------------------------- recommendation signals */

export interface RecommendationSignal {
  id: string
  label: string
  detail: string
  enabled: boolean
  /** Signals a student can switch off in their own settings. */
  studentControllable: boolean
}

export const recommendationSignals: RecommendationSignal[] = [
  {
    id: 'rs-1',
    label: 'Same department or programme',
    detail: 'Used for people and community suggestions.',
    enabled: true,
    studentControllable: true,
  },
  {
    id: 'rs-2',
    label: 'Shared communities',
    detail: 'Counts communities both accounts belong to. Private community membership is never named as a reason.',
    enabled: true,
    studentControllable: true,
  },
  {
    id: 'rs-3',
    label: 'Declared interests',
    detail: 'Only interests the student added themselves.',
    enabled: true,
    studentControllable: true,
  },
  {
    id: 'rs-4',
    label: 'Mutual connections',
    detail: 'Counts, never names, unless both accounts are connected to the person.',
    enabled: true,
    studentControllable: true,
  },
  {
    id: 'rs-5',
    label: 'Event attendance',
    detail: 'Confirmed attendance at the same event.',
    enabled: true,
    studentControllable: true,
  },
  {
    id: 'rs-6',
    label: 'Approximate location',
    detail: 'Permanently off. Housing area is private and is never a recommendation reason.',
    enabled: false,
    studentControllable: false,
  },
  {
    id: 'rs-7',
    label: 'Roommate preferences',
    detail: 'Permanently off outside roommate matching, where the student controls disclosure.',
    enabled: false,
    studentControllable: false,
  },
]
